export const MODULE_INTERACTION_EVENT = "handyout:module-interaction";

export type ModuleInteractionEventType =
  | "click"
  | "select"
  | "deselect"
  | "tab_change"
  | "search"
  | "sort"
  | "column_toggle"
  | "page_change";

export type ModuleInteractionTargetKind =
  | "legend-item"
  | "bar"
  | "tab"
  | "node"
  | "segment"
  | "series"
  | "slice"
  | "point"
  | "cell"
  | "row"
  | "step"
  | "card"
  | "link"
  | "control";

export type ModuleInteractionTarget = {
  kind: ModuleInteractionTargetKind;
  id?: string | number | null;
  index?: number | null;
  group?: string | number | null;
};

export type ModuleInteractionPayload = Record<
  string,
  number | string | boolean | null
>;

export type ModuleInteractionDetail = {
  event: ModuleInteractionEventType;
  payload: ModuleInteractionPayload;
};

// Payloads must stay free of viewer- or author-authored display text: they end
// up in the analytics DB where only structural values (kinds, ids, indices)
// are allowed. Labels are free-typed by the author, so they are neither a
// stable key nor guaranteed to be free of personal data.
export function emitModuleInteraction(
  element: Element | null | undefined,
  event: ModuleInteractionEventType,
  target: ModuleInteractionTarget,
  extra?: ModuleInteractionPayload,
): void {
  if (!element) return;
  const payload: ModuleInteractionPayload = {
    ...(extra ?? {}),
    targetKind: target.kind,
    targetId: target.id ?? null,
    targetIndex: target.index ?? null,
  };
  if (target.group !== undefined) {
    payload.targetGroup = target.group;
  }
  element.dispatchEvent(
    new CustomEvent<ModuleInteractionDetail>(MODULE_INTERACTION_EVENT, {
      bubbles: true,
      detail: { event, payload },
    }),
  );
}

export const MODULE_SURFACE_EVENT = "handyout:module-surface";

export type ModuleSurfaceKind = "overlay" | "inline";

export type ModuleSurfaceState = "open" | "close";

export type ModuleSurfaceDetail = {
  state: ModuleSurfaceState;
  surface: string;
  kind: ModuleSurfaceKind;
};

/**
 * Signals that a module-owned surface opened or closed, for the reader's
 * attention tracker. `overlay` is a modal surface (lightbox, sheet, drawer)
 * that holds the reader exclusively while open; `inline` is an expanded state
 * inside the module (accordion, pinned detail) that keeps the module engaged
 * while it has focus. Dispatch from an element inside the module so the event
 * bubbles through its `[data-module-id]` wrapper. The editor and previews mount
 * no listener, so emitting there is a no-op.
 */
export function emitModuleSurface(
  element: Element | null | undefined,
  state: ModuleSurfaceState,
  surface: string,
  kind: ModuleSurfaceKind,
): void {
  if (!element) return;
  element.dispatchEvent(
    new CustomEvent<ModuleSurfaceDetail>(MODULE_SURFACE_EVENT, {
      bubbles: true,
      detail: { state, surface, kind },
    }),
  );
}
